<template>
  <div class="app-container leader-hunter-page">
    <div class="page-heading">
      <div>
        <h2>尋找領隊與導遊</h2>
        <p>僅列出已付費、尚在有效期且開放履歷的會員。</p>
      </div>
      <div class="heading-actions">
        <el-button
          v-if="canManageSettings"
          data-testid="tour-leader-settings-entry"
          icon="el-icon-setting"
          @click="openSettings"
        >
          專區設定
        </el-button>
        <el-button
          v-if="canUseAgencyCommunication && !featureDisabled"
          data-testid="agency-communication-history"
          icon="el-icon-time"
          @click="openHistory"
        >
          邀約與聊天歷史
        </el-button>
        <el-tooltip content="徵才啟事" placement="left">
          <el-button
            class="jobs-entry"
            type="warning"
            icon="el-icon-suitcase"
            circle
            aria-label="徵才啟事"
            :disabled="featureDisabled"
            @click="openJobs"
          />
        </el-tooltip>
      </div>
    </div>

    <el-alert
      v-if="featureDisabled"
      data-testid="tour-leader-feature-disabled"
      class="load-alert"
      type="warning"
      title="此旅行社的領隊導遊專區目前已關閉。"
      description="搜尋、徵才、邀約與聊天已停用；如需開放，請聯絡系統管理員。"
      :closable="false"
      show-icon
    >
      <el-button slot="default" size="mini" @click="fetchProfiles">重新檢查</el-button>
    </el-alert>

    <el-card v-if="!featureDisabled" shadow="never" class="filter-card">
      <el-form :inline="true" :model="query" @submit.native.prevent>
        <el-form-item label="關鍵字">
          <el-input
            v-model.trim="query.keyword"
            maxlength="50"
            clearable
            placeholder="姓名或暱稱"
            @keyup.enter.native="search"
          />
        </el-form-item>
        <el-form-item label="身分">
          <el-select v-model="query.role" clearable placeholder="全部">
            <el-option label="領隊" value="leader" />
            <el-option label="導遊" value="guide" />
          </el-select>
        </el-form-item>
        <el-form-item label="性別">
          <el-select v-model="query.genders" multiple collapse-tags clearable placeholder="全部">
            <el-option label="男" value="male" />
            <el-option label="女" value="female" />
          </el-select>
        </el-form-item>
        <el-form-item label="年齡區間">
          <el-select v-model="query.ageRanges" multiple collapse-tags clearable placeholder="全部">
            <el-option label="30 歲以下" value="under30" />
            <el-option label="30–39 歲" value="30-39" />
            <el-option label="40–49 歲" value="40-49" />
            <el-option label="50–59 歲" value="50-59" />
            <el-option label="60 歲以上" value="60plus" />
          </el-select>
        </el-form-item>
        <el-form-item label="帶團區域">
          <el-select
            v-model="query.guidanceAreas"
            multiple
            filterable
            allow-create
            default-first-option
            collapse-tags
            clearable
            placeholder="請輸入區域"
          />
        </el-form-item>
        <el-form-item label="帶團年資">
          <el-select
            v-model="query.guidanceYearRanges"
            multiple
            collapse-tags
            clearable
            placeholder="全部"
          >
            <el-option label="1 年以下" value="under1" />
            <el-option label="1–3 年" value="1-3" />
            <el-option label="4–7 年" value="4-7" />
            <el-option label="8–10 年" value="8-10" />
            <el-option label="10 年以上" value="over10" />
          </el-select>
        </el-form-item>
        <el-form-item label="居住縣市">
          <el-select
            v-model="query.residences"
            multiple
            filterable
            allow-create
            default-first-option
            collapse-tags
            clearable
            placeholder="請輸入縣市"
          />
        </el-form-item>
        <el-form-item label="協會會員">
          <el-select v-model="query.associationMember" clearable placeholder="全部">
            <el-option label="是" :value="true" />
            <el-option label="否" :value="false" />
          </el-select>
        </el-form-item>
        <el-form-item label="可配合日期">
          <el-date-picker
            v-model="query.availableDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="選擇日期"
          />
        </el-form-item>
        <el-form-item label="排序">
          <el-select v-model="query.sort">
            <el-option label="推薦優先" value="recommended" />
            <el-option label="最近更新" value="updated" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search">
            查詢
          </el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="reset">重設</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-alert
      v-if="loadError"
      class="load-alert"
      type="error"
      :title="loadError"
      :closable="false"
      show-icon
    >
      <el-button slot="default" size="mini" @click="fetchProfiles">重新載入</el-button>
    </el-alert>

    <el-table
      v-if="!featureDisabled"
      v-loading="loading"
      :data="profiles"
      border
      stripe
      empty-text="目前沒有符合條件的領隊或導遊"
    >
      <el-table-column label="人才" min-width="220">
        <template slot-scope="scope">
          <div class="profile-cell">
            <el-avatar :size="52" :src="scope.row.avatarUrl || emptyAvatar" />
            <div>
              <strong>{{ displayName(scope.row) }}</strong>
              <div class="secondary-text">{{ roleLabel(scope.row) }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="基本資料" min-width="170">
        <template slot-scope="scope">
          <span>{{ basicInfo(scope.row) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="帶團區域與年資" min-width="220">
        <template slot-scope="scope">
          <div>{{ listText(scope.row.guidanceAreas || scope.row.guidanceAreaNames) }}</div>
          <small class="secondary-text">{{
            scope.row.guidanceYearsLabel || scope.row.guidanceYears || '未提供'
          }}</small>
        </template>
      </el-table-column>
      <el-table-column label="居住地" min-width="120">
        <template slot-scope="scope">{{
          scope.row.residence || scope.row.positionName || '未提供'
        }}</template>
      </el-table-column>
      <el-table-column label="協會" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.associationMember" size="mini" type="success">會員</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column v-if="canManageSettings" label="推薦" width="110" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.isRecommended" size="mini" type="success">
            順序 {{ scope.row.recommendSort }}
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column
        label="操作"
        :width="canManageSettings ? 280 : 180"
        align="center"
        fixed="right"
      >
        <template slot-scope="scope">
          <el-button size="mini" @click="showProfile(scope.row)">詳情</el-button>
          <el-button
            v-if="canManageSettings"
            data-testid="profile-recommendation-entry"
            size="mini"
            type="warning"
            @click="openRecommendation(scope.row)"
          >
            推薦
          </el-button>
          <el-button
            v-if="canUseAgencyCommunication"
            type="primary"
            size="mini"
            @click="openContact(scope.row)"
          >
            邀約
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      v-show="!featureDisabled && total > 0"
      :total="total"
      :page.sync="query.page"
      :limit.sync="query.limit"
      @pagination="fetchProfiles"
    />

    <el-dialog
      v-if="canManageSettings"
      title="領隊導遊專區設定"
      :visible.sync="settingsDialogVisible"
      width="860px"
    >
      <el-tabs v-model="settingsActiveTab">
        <el-tab-pane label="旅行社開放設定" name="agencies">
          <el-form :inline="true" @submit.native.prevent>
            <el-form-item label="旅行社">
              <el-input
                v-model.trim="settingsQuery.keyword"
                maxlength="100"
                clearable
                placeholder="名稱或代碼"
                @keyup.enter.native="searchSettings"
              />
            </el-form-item>
            <el-form-item>
              <el-button
                type="primary"
                icon="el-icon-search"
                :loading="settingsLoading"
                @click="searchSettings"
              >
                查詢
              </el-button>
            </el-form-item>
          </el-form>
          <el-alert
            v-if="settingsError"
            type="error"
            :title="settingsError"
            :closable="false"
            show-icon
          />
          <el-table
            v-loading="settingsLoading"
            :data="agencySettings"
            border
            empty-text="目前沒有可設定的旅行社"
          >
            <el-table-column prop="agencyName" label="旅行社" min-width="220" />
            <el-table-column prop="agencyCode" label="代碼" min-width="150" />
            <el-table-column label="專區入口" width="130" align="center">
              <template slot-scope="scope">
                <el-switch
                  :value="scope.row.isEnabled"
                  :disabled="Boolean(settingsSaving[scope.row.agencyCode])"
                  active-text="開放"
                  inactive-text="關閉"
                  @change="saveAgencySetting(scope.row, $event)"
                />
              </template>
            </el-table-column>
            <el-table-column prop="updatedAt" label="更新時間" min-width="180" />
            <el-table-column prop="updatedBy" label="更新者" min-width="130" />
          </el-table>
          <pagination
            v-show="settingsTotal > 0"
            :total="settingsTotal"
            :page.sync="settingsQuery.page"
            :limit.sync="settingsQuery.pageSize"
            @pagination="loadSettings"
          />
        </el-tab-pane>
        <el-tab-pane label="專區輪播" name="banners">
          <el-alert
            type="info"
            title="專區輪播沿用現有廣告管理與版位，圖片、排序及上下架時間請在廣告管理維護。"
            :closable="false"
            show-icon
          />
          <div class="banner-toolbar">
            <span>版位代碼：{{ bannerSlotCode || '尚未設定' }}</span>
            <el-button type="primary" size="mini" @click="openAdvertisementManagement">
              前往廣告管理
            </el-button>
          </div>
          <el-alert
            v-if="bannersError"
            type="error"
            :title="bannersError"
            :closable="false"
            show-icon
          />
          <el-table v-loading="bannersLoading" :data="banners" border empty-text="目前無輪播資料">
            <el-table-column prop="title" label="名稱" min-width="220" />
            <el-table-column prop="sort" label="排序" width="80" align="center" />
            <el-table-column label="狀態" width="90" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.isEnabled ? 'success' : 'info'" size="mini">
                  {{ scope.row.isEnabled ? '啟用' : '停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="startAt" label="上架時間" min-width="170" />
            <el-table-column prop="endAt" label="下架時間" min-width="170" />
          </el-table>
        </el-tab-pane>
      </el-tabs>
      <span slot="footer">
        <el-button @click="settingsDialogVisible = false">關閉</el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-if="canManageSettings"
      title="人才推薦設定"
      :visible.sync="recommendationDialogVisible"
      width="440px"
    >
      <el-form label-width="100px">
        <el-form-item label="人才">{{ displayName(recommendationTarget) }}</el-form-item>
        <el-form-item label="推薦">
          <el-switch v-model="recommendationForm.isRecommended" />
        </el-form-item>
        <el-form-item label="推薦排序">
          <el-input-number
            v-model="recommendationForm.recommendSort"
            :min="0"
            :max="9999"
            :step="1"
          />
          <div class="secondary-text">數字越小越優先，預設 99。</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="recommendationDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="recommendationSaving" @click="saveRecommendation">
          儲存
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="領隊導遊公開履歷" :visible.sync="profileDialogVisible" width="720px">
      <div v-loading="profileLoading">
        <el-alert
          v-if="profileError"
          type="error"
          :title="profileError"
          :closable="false"
          show-icon
        />
        <el-descriptions v-else-if="selectedProfile" :column="1" border>
          <el-descriptions-item label="姓名">{{
            displayName(selectedProfile)
          }}</el-descriptions-item>
          <el-descriptions-item label="身分">{{ roleLabel(selectedProfile) }}</el-descriptions-item>
          <el-descriptions-item label="個人簡介">{{
            selectedProfile.selfDescription || '未提供'
          }}</el-descriptions-item>
          <el-descriptions-item label="經歷">{{
            selectedProfile.experience || '未提供'
          }}</el-descriptions-item>
          <el-descriptions-item label="專長">{{
            listText(selectedProfile.specialties)
          }}</el-descriptions-item>
          <el-descriptions-item label="近期帶團">
            <ul v-if="selectedProfile.recentTravels && selectedProfile.recentTravels.length">
              <li
                v-for="travel in selectedProfile.recentTravels"
                :key="travel.id || travel.travelId"
              >
                {{ travel.name || travel.travelName }}
              </li>
            </ul>
            <span v-else>無可公開資料</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <span slot="footer">
        <el-button @click="profileDialogVisible = false">關閉</el-button>
        <el-button
          v-if="selectedProfile && canUseAgencyCommunication"
          type="primary"
          @click="openContact(selectedProfile)"
          >邀約或傳訊</el-button
        >
      </span>
    </el-dialog>

    <el-dialog
      v-if="canUseAgencyCommunication"
      title="建立邀約或第一則訊息"
      :visible.sync="contactDialogVisible"
      width="620px"
    >
      <el-alert
        title="必須選擇屬於目前旅行社的旅行團；系統將由登入身分決定旅行社範圍。"
        type="info"
        :closable="false"
        show-icon
      />
      <el-form ref="contactForm" :model="contactForm" :rules="contactRules" label-width="110px">
        <el-form-item label="對象">{{
          contactTarget ? displayName(contactTarget) : ''
        }}</el-form-item>
        <el-form-item label="聯絡方式" prop="contactType">
          <el-radio-group v-model="contactForm.contactType">
            <el-radio-button label="message">傳送訊息</el-radio-button>
            <el-radio-button label="invitation">建立邀約</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="旅行團" prop="travelId">
          <el-select
            v-model="contactForm.travelId"
            filterable
            style="width: 100%"
            :loading="travelLoading"
            placeholder="請選擇旅行團"
          >
            <el-option
              v-for="travel in travels"
              :key="travel.id || travel.travelId"
              :label="travel.name || travel.travelName"
              :value="travel.id || travel.travelId"
            />
          </el-select>
          <div v-if="travelError" class="form-error">{{ travelError }}</div>
        </el-form-item>
        <template v-if="contactForm.contactType === 'invitation'">
          <el-form-item label="邀約標題" prop="title">
            <el-input v-model.trim="contactForm.title" maxlength="120" show-word-limit />
          </el-form-item>
          <el-form-item label="工作日期" prop="workDates">
            <el-date-picker
              v-model="contactForm.workDates"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="開始日"
              end-placeholder="結束日"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item label="回覆期限" prop="expiresAt">
            <el-date-picker
              v-model="contactForm.expiresAt"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              style="width: 100%"
            />
          </el-form-item>
        </template>
        <el-form-item label="訊息" prop="content">
          <el-input
            v-model.trim="contactForm.content"
            type="textarea"
            :rows="5"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="contactDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="contactSubmitting" @click="submitContact"
          >送出</el-button
        >
      </span>
    </el-dialog>

    <el-dialog
      v-if="canUseAgencyCommunication"
      title="邀約與聊天歷史"
      :visible.sync="historyDialogVisible"
      width="900px"
    >
      <el-alert
        v-if="historyError"
        type="error"
        :title="historyError"
        :closable="false"
        show-icon
      />
      <el-tabs v-model="activeHistoryTab">
        <el-tab-pane label="聊天紀錄" name="conversations">
          <el-table v-loading="historyLoading" :data="conversations" empty-text="尚無聊天歷史">
            <el-table-column prop="travelName" label="旅行團" min-width="180" />
            <el-table-column prop="memberName" label="領隊／導遊" min-width="120" />
            <el-table-column
              prop="lastMessagePreview"
              label="最後訊息"
              min-width="220"
              show-overflow-tooltip
            />
            <el-table-column prop="unreadCount" label="未讀" width="70" align="center" />
            <el-table-column prop="lastMessageAt" label="最後聯絡" width="170" />
            <el-table-column label="操作" width="90" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" @click="openConversation(scope.row)">開啟</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="邀約紀錄" name="invitations">
          <el-table v-loading="invitationLoading" :data="invitations" empty-text="尚無邀約紀錄">
            <el-table-column prop="travelName" label="旅行團" min-width="170" />
            <el-table-column prop="memberName" label="領隊／導遊" min-width="120" />
            <el-table-column prop="title" label="邀約標題" min-width="180" show-overflow-tooltip />
            <el-table-column label="狀態" width="100" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" :type="invitationStatusType(scope.row.status)">
                  {{ invitationStatusLabel(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="sentAt" label="送出時間" width="170" />
            <el-table-column label="操作" width="90" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button
                  v-if="canCancelInvitation(scope.row)"
                  type="text"
                  class="danger-action"
                  @click="cancelInvitation(scope.row)"
                >
                  撤回
                </el-button>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <el-dialog
      v-if="canUseAgencyCommunication"
      title="聊天室"
      :visible.sync="chatDialogVisible"
      width="860px"
      append-to-body
      custom-class="hunter-chat-dialog"
      @closed="closeConversation"
    >
      <template v-if="selectedConversation">
        <div class="hunter-chat-header">
          <span class="hunter-chat-name">{{ selectedConversation.memberName || '領隊／導遊' }}</span>
          <span class="hunter-chat-divider">丨</span>
          <span class="hunter-chat-travel">{{ selectedConversation.travelName || '旅行團名稱未提供' }}</span>
        </div>
        <el-alert
          v-if="conversationReadOnly"
          title="會員資格已過期或已停權，此對話目前僅可閱讀。"
          type="warning"
          :closable="false"
          show-icon
        />
        <el-alert
          v-if="chatError"
          class="load-alert"
          type="error"
          :title="chatError"
          :closable="false"
          show-icon
        />
        <div v-loading="chatLoading" class="message-list">
          <div v-if="!chatLoading && messages.length === 0" class="empty-message">尚無訊息</div>
          <template v-for="item in groupedChatMessages">
            <div v-if="item.divider" :key="'date-' + item.key" class="hunter-chat-date">
              <el-tag type="info" size="small">{{ item.label }}</el-tag>
            </div>
            <div
              v-else
              :key="item.key"
              class="message-item"
              :class="{ 'message-agency': isAgencyMessage(item.message) }"
            >
              <div v-if="!isAgencyMessage(item.message)" class="message-sender">
                <span class="sender-avatar">{{ messageSenderLabel(item.message).slice(0, 1) }}</span>
                <span class="sender-name">{{ messageSenderLabel(item.message) }}</span>
              </div>
              <div class="message-row">
                <div class="message-content" :class="{ 'message-image-body': isImageMessage(item.message) }">
                  <template v-if="isImageMessage(item.message)">
                    <img
                      v-if="chatImageUrls[chatMediaKey(item.message)]"
                      class="chat-image"
                      :src="chatImageUrls[chatMediaKey(item.message)]"
                      alt="聊天圖片"
                    />
                    <span v-else-if="chatImageLoading[chatMediaKey(item.message)]">
                      <i class="el-icon-loading" /> 圖片載入中…
                    </span>
                    <el-button
                      v-else
                      type="text"
                      icon="el-icon-refresh"
                      @click="loadProtectedImage(item.message)"
                    >
                      {{ chatImageErrors[chatMediaKey(item.message)] || '載入圖片' }}
                    </el-button>
                  </template>
                  <template v-else>{{ item.message.content || item.message.text || '' }}</template>
                </div>
                <span class="message-time">{{ (item.message.createdAt || item.message.sentAt || '').slice(11, 16) }}</span>
              </div>
            </div>
          </template>
        </div>
        <div v-if="failedImageFile || failedChatEnvelope" class="chat-retry-row">
          <el-button
            v-if="failedImageFile"
            type="text"
            :disabled="chatUploading || chatSubmitting"
            @click="retryImageUpload"
          >
            重試上傳
          </el-button>
          <el-button
            v-if="failedChatEnvelope"
            type="text"
            :disabled="chatUploading || chatSubmitting"
            @click="retryFailedChatMessage"
          >
            重試傳送上一則訊息
          </el-button>
        </div>
        <div class="chat-input-row">
          <input
            ref="chatImageInput"
            class="visually-hidden-file"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            :disabled="conversationReadOnly || chatSubmitting || chatUploading"
            @change="selectChatImage"
          />
          <el-input
            v-model.trim="chatInput"
            placeholder="輸入文字"
            maxlength="1000"
            :disabled="conversationReadOnly || chatSubmitting"
            @keyup.enter.native="sendConversationMessage"
          >
            <el-button
              slot="prepend"
              icon="el-icon-picture-outline"
              :loading="chatUploading"
              :disabled="conversationReadOnly || chatSubmitting || chatUploading"
              @click="$refs.chatImageInput && $refs.chatImageInput.click()"
            >
              上傳
            </el-button>
            <el-button
              slot="append"
              icon="el-icon-s-promotion"
              :loading="chatSubmitting"
              :disabled="conversationReadOnly || chatSubmitting || !chatInput"
              @click="sendConversationMessage"
            />
          </el-input>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import {
  getTourLeaderBanners,
  getTourLeaderSettings,
  updateTourLeaderProfileRecommendation,
  updateTourLeaderSetting,
} from '@/api/tourLeaderAdmin'
import {
  cancelAgencyInvitation,
  createAgencyConversation,
  createAgencyInvitation,
  getAgencyConversations,
  getAgencyConversationMessages,
  getAgencyFeatureTravels,
  getAgencyInvitations,
  getLeaderProfile,
  getLeaderProfiles,
  downloadAgencyConversationMedia,
  markAgencyConversationRead,
  sendAgencyConversationMessage,
  uploadAgencyConversationMedia,
} from '@/api/tourLeaderAgency'
import { normalizePhaseThreeResponse, toPhaseThreePagedResult } from '@/utils/tourLeaderApi'
import {
  isTourLeaderFeatureDisabled,
  normalizePhaseSevenResponse,
  normalizeTourLeaderBanners,
  normalizeTourLeaderRecommendation,
  normalizeTourLeaderSetting,
  toTourLeaderSettingsPage,
} from '@/utils/tourLeaderPhaseSeven'
import {
  buildLeaderProfileSearchParams,
  canCancelInvitation,
  createConversationRequestGuard,
  createLatestConversationListRequestGuard,
  deliveredInvitation,
  findConversationByTrustedId,
  invitationStatusLabel,
  invitationStatusType,
  isConversationReadOnly,
  normalizeAgencyConversation,
  normalizeLeaderProfile,
  refreshCurrentConversationReadState,
  restoreLeaderHunterSearchState,
  serializeLeaderHunterSearchState,
  synchronizeConversationUnreadCount,
} from '@/utils/tourLeaderAgencyUi'
import {
  canUseTourLeaderAgencyCommunication,
  hasAnyRole,
  TOUR_LEADER_MANAGEMENT_ROLES,
  TOUR_LEADER_SETTINGS_ADMIN_ROLES,
} from '@/utils/tourLeaderPermissions'
import {
  protectedMediaBody,
  revokeProtectedMediaUrls,
  validateTourLeaderChatImage,
} from '@/utils/tourLeaderChatMedia'
import { tourLeaderAgencyRealtime } from '@/services/tourLeaderAgencyRealtime'

const searchStateKey = 'tour-leader-hunter-search-v1'

function defaultQuery() {
  return {
    keyword: '',
    role: '',
    genders: [],
    ageRanges: [],
    guidanceAreas: [],
    guidanceYearRanges: [],
    residences: [],
    associationMember: '',
    availableDate: '',
    sort: 'recommended',
    page: 1,
    limit: 20,
  }
}

export default {
  name: 'AgencyLeaderHunter',
  components: { Pagination },
  data() {
    return {
      query: defaultQuery(),
      profiles: [],
      total: 0,
      loading: false,
      loadError: '',
      featureDisabled: false,
      featureStateGeneration: 0,
      profileListRequestSerial: 0,
      profileRequestSerial: 0,
      emptyAvatar: `${process.env.VUE_APP_S3_LINK}/base/temp_images/empty-user.png`,
      settingsDialogVisible: false,
      settingsActiveTab: 'agencies',
      settingsQuery: { keyword: '', page: 1, pageSize: 20 },
      agencySettings: [],
      settingsTotal: 0,
      settingsLoading: false,
      settingsError: '',
      settingsRequestSerial: 0,
      settingsSaving: {},
      banners: [],
      bannerSlotCode: '',
      bannersLoading: false,
      bannersError: '',
      bannersRequestSerial: 0,
      recommendationDialogVisible: false,
      recommendationTarget: null,
      recommendationForm: { isRecommended: false, recommendSort: 99 },
      recommendationSaving: false,
      profileDialogVisible: false,
      profileLoading: false,
      profileError: '',
      selectedProfile: null,
      contactDialogVisible: false,
      contactTarget: null,
      contactSubmitting: false,
      contactRequestSerial: 0,
      travelLoading: false,
      travelError: '',
      travelRequestSerial: 0,
      travels: [],
      contactForm: {
        contactType: 'message',
        travelId: null,
        title: '',
        workDates: [],
        expiresAt: '',
        content: '',
      },
      contactRules: {
        contactType: [{ required: true, message: '請選擇聯絡方式', trigger: 'change' }],
        travelId: [{ required: true, message: '請選擇旅行團', trigger: 'change' }],
        title: [
          {
            validator: (rule, value, callback) => {
              if (this.contactForm.contactType === 'invitation' && !value) {
                callback(new Error('請輸入邀約標題'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        workDates: [
          {
            validator: (rule, value, callback) => {
              if (this.contactForm.contactType === 'invitation' && (!value || value.length !== 2)) {
                callback(new Error('請選擇工作日期'))
              } else {
                callback()
              }
            },
            trigger: 'change',
          },
        ],
        content: [{ required: true, message: '請輸入訊息', trigger: 'blur' }],
        expiresAt: [
          {
            validator: (rule, value, callback) => {
              if (this.contactForm.contactType !== 'invitation') {
                callback()
                return
              }
              if (!value) {
                callback(new Error('請選擇回覆期限'))
                return
              }
              const normalized = value.replace(' ', 'T') + '+08:00'
              if (
                !Number.isFinite(Date.parse(normalized)) ||
                Date.parse(normalized) <= Date.now()
              ) {
                callback(new Error('回覆期限必須晚於現在'))
                return
              }
              callback()
            },
            trigger: 'change',
          },
        ],
      },
      historyDialogVisible: false,
      historyLoading: false,
      historyError: '',
      conversations: [],
      conversationListRequestGuard: createLatestConversationListRequestGuard(),
      invitations: [],
      invitationLoading: false,
      invitationRequestSerial: 0,
      activeHistoryTab: 'conversations',
      chatDialogVisible: false,
      selectedConversation: null,
      messages: [],
      chatInput: '',
      chatLoading: false,
      chatSubmitting: false,
      chatUploading: false,
      chatError: '',
      activeConversationMarkedRead: false,
      failedImageFile: null,
      failedChatEnvelope: null,
      chatImageUrls: {},
      chatImageLoading: {},
      chatImageErrors: {},
      chatImageGeneration: 0,
      chatUploadGeneration: 0,
      chatPollTimer: null,
      releaseChatSocket: null,
      unsubscribeChatSocket: null,
      conversationRequestGuard: createConversationRequestGuard(),
      handledRouteIntent: '',
      routeIntentSerial: 0,
    }
  },
  created() {
    if (!hasAnyRole(this.$store.getters.roles, TOUR_LEADER_MANAGEMENT_ROLES)) {
      this.$router.replace('/401')
      return
    }
    this.restoreSearchState()
    this.fetchProfiles().then(() => this.handleRouteIntent())
  },
  activated() {
    this.handleRouteIntent()
  },
  deactivated() {
    this.routeIntentSerial += 1
    this.handledRouteIntent = ''
  },
  beforeDestroy() {
    this.profileListRequestSerial += 1
    this.profileRequestSerial += 1
    this.settingsRequestSerial += 1
    this.bannersRequestSerial += 1
    this.contactRequestSerial += 1
    this.travelRequestSerial += 1
    this.invitationRequestSerial += 1
    this.routeIntentSerial += 1
    this.conversationListRequestGuard.invalidate()
    this.closeConversation()
  },
  methods: {
    restoreSearchState() {
      try {
        this.query = restoreLeaderHunterSearchState(
          window.sessionStorage.getItem(searchStateKey),
          defaultQuery()
        )
      } catch (error) {
        window.sessionStorage.removeItem(searchStateKey)
      }
    },
    saveSearchState() {
      window.sessionStorage.setItem(
        searchStateKey,
        serializeLeaderHunterSearchState(this.query, defaultQuery())
      )
    },
    async fetchProfiles() {
      const requestSerial = ++this.profileListRequestSerial
      const featureGeneration = this.featureStateGeneration
      this.loading = true
      this.loadError = ''
      this.saveSearchState()
      try {
        const result = toPhaseThreePagedResult(
          await getLeaderProfiles(buildLeaderProfileSearchParams(this.query))
        )
        if (
          requestSerial !== this.profileListRequestSerial ||
          featureGeneration !== this.featureStateGeneration
        ) {
          return false
        }
        this.profiles = result.items.map(normalizeLeaderProfile)
        this.total = result.total
        this.featureDisabled = false
        return true
      } catch (error) {
        if (requestSerial !== this.profileListRequestSerial) return false
        this.profiles = []
        this.total = 0
        if (!this.handleFeatureDisabled(error)) {
          this.loadError = '無法載入領隊導遊資料，請稍後再試。'
        }
        return false
      } finally {
        if (requestSerial === this.profileListRequestSerial) this.loading = false
      }
    },
    search() {
      this.query.page = 1
      this.fetchProfiles()
    },
    reset() {
      this.query = defaultQuery()
      this.fetchProfiles()
    },
    openJobs() {
      if (this.featureDisabled) return false
      this.saveSearchState()
      this.$router.push({ name: 'AgencyLeaderJobs' })
    },
    handleFeatureDisabled(error) {
      if (!isTourLeaderFeatureDisabled(error)) return false
      this.featureStateGeneration += 1
      this.featureDisabled = true
      this.profileListRequestSerial += 1
      this.profileRequestSerial += 1
      this.contactRequestSerial += 1
      this.travelRequestSerial += 1
      this.invitationRequestSerial += 1
      this.routeIntentSerial += 1
      this.conversationListRequestGuard.invalidate()
      this.profiles = []
      this.total = 0
      this.loadError = ''
      this.loading = false
      this.selectedProfile = null
      this.profileDialogVisible = false
      this.profileLoading = false
      this.contactDialogVisible = false
      this.contactSubmitting = false
      this.travelLoading = false
      this.historyDialogVisible = false
      this.historyLoading = false
      this.invitationLoading = false
      this.contactTarget = null
      this.travels = []
      this.conversations = []
      this.invitations = []
      this.handledRouteIntent = ''
      this.closeConversation()
      return true
    },
    async openSettings() {
      if (!this.canManageSettings) return false
      this.settingsDialogVisible = true
      this.settingsActiveTab = 'agencies'
      await Promise.all([this.loadSettings(), this.loadBanners()])
      return true
    },
    searchSettings() {
      if (!this.canManageSettings) return false
      this.settingsQuery.page = 1
      return this.loadSettings()
    },
    async loadSettings() {
      if (!this.canManageSettings) return false
      const requestSerial = ++this.settingsRequestSerial
      this.settingsLoading = true
      this.settingsError = ''
      try {
        const result = toTourLeaderSettingsPage(await getTourLeaderSettings(this.settingsQuery))
        if (requestSerial !== this.settingsRequestSerial) return false
        this.agencySettings = result.items
        this.settingsTotal = result.total
        return true
      } catch (error) {
        if (requestSerial !== this.settingsRequestSerial) return false
        this.agencySettings = []
        this.settingsTotal = 0
        this.settingsError = '無法載入旅行社開放設定。'
        return false
      } finally {
        if (requestSerial === this.settingsRequestSerial) this.settingsLoading = false
      }
    },
    async saveAgencySetting(setting, isEnabled) {
      if (!this.canManageSettings || !setting || !setting.agencyCode) return false
      const agencyCode = setting.agencyCode
      if (this.settingsSaving[agencyCode]) return false
      this.$set(this.settingsSaving, agencyCode, true)
      try {
        const response = normalizePhaseSevenResponse(
          await updateTourLeaderSetting(agencyCode, { isEnabled })
        )
        const updated = normalizeTourLeaderSetting(
          response || { ...setting, isEnabled: isEnabled === true }
        )
        const index = this.agencySettings.findIndex(item => item.agencyCode === agencyCode)
        if (index >= 0) this.$set(this.agencySettings, index, updated)
        this.$message.success(isEnabled ? '已開放專區入口' : '已關閉專區入口')
        return true
      } catch (error) {
        this.$message.error('專區開放設定儲存失敗。')
        return false
      } finally {
        this.$delete(this.settingsSaving, agencyCode)
      }
    },
    async loadBanners() {
      if (!this.canManageSettings) return false
      const requestSerial = ++this.bannersRequestSerial
      this.bannersLoading = true
      this.bannersError = ''
      try {
        const result = normalizeTourLeaderBanners(await getTourLeaderBanners())
        if (requestSerial !== this.bannersRequestSerial) return false
        this.banners = result.items
        this.bannerSlotCode = result.slotCode
        return true
      } catch (error) {
        if (requestSerial !== this.bannersRequestSerial) return false
        this.banners = []
        this.bannerSlotCode = ''
        this.bannersError = '無法載入專區輪播資訊。'
        return false
      } finally {
        if (requestSerial === this.bannersRequestSerial) this.bannersLoading = false
      }
    },
    openAdvertisementManagement() {
      if (!this.canManageSettings) return false
      this.settingsDialogVisible = false
      this.$router.push({
        name: 'AdvertisementCustomerCompany',
        query: this.bannerSlotCode ? { slotCode: this.bannerSlotCode } : {},
      })
      return true
    },
    openRecommendation(profile) {
      if (!this.canManageSettings || !profile) return false
      const normalized = normalizeLeaderProfile(profile)
      this.recommendationTarget = normalized
      this.recommendationForm = {
        isRecommended: normalized.isRecommended === true,
        recommendSort: Number.isInteger(Number(normalized.recommendSort))
          ? Number(normalized.recommendSort)
          : 99,
      }
      this.recommendationDialogVisible = true
      return true
    },
    async saveRecommendation() {
      if (!this.canManageSettings || !this.recommendationTarget) return false
      const userId = Number(
        this.recommendationTarget.userId ||
          this.recommendationTarget.userID ||
          this.recommendationTarget.id
      )
      const recommendSort = Number(this.recommendationForm.recommendSort)
      if (!Number.isInteger(userId) || userId <= 0 || !Number.isInteger(recommendSort)) {
        this.$message.error('人才或推薦排序格式不正確。')
        return false
      }
      this.recommendationSaving = true
      try {
        const payload = {
          isRecommended: this.recommendationForm.isRecommended === true,
          recommendSort,
        }
        const response = normalizePhaseSevenResponse(
          await updateTourLeaderProfileRecommendation(userId, payload)
        )
        const updated = normalizeTourLeaderRecommendation(response || { userId, ...payload })
        this.profiles = this.profiles.map(profile =>
          Number(profile.userId || profile.userID || profile.id) === userId
            ? { ...profile, ...updated }
            : profile
        )
        if (
          this.selectedProfile &&
          Number(
            this.selectedProfile.userId || this.selectedProfile.userID || this.selectedProfile.id
          ) === userId
        ) {
          this.selectedProfile = { ...this.selectedProfile, ...updated }
        }
        this.recommendationTarget = { ...this.recommendationTarget, ...updated }
        this.recommendationDialogVisible = false
        this.$message.success('人才推薦設定已更新')
        return true
      } catch (error) {
        this.$message.error('人才推薦設定儲存失敗。')
        return false
      } finally {
        this.recommendationSaving = false
      }
    },
    async handleRouteIntent() {
      if (this.featureDisabled) return false
      const query = (this.$route && this.$route.query) || {}
      const memberUserId = Number(query.memberUserId)
      const requestedAction = ['profile', 'contact', 'chat'].includes(query.action)
        ? query.action
        : 'profile'
      const action =
        requestedAction === 'profile' || this.canUseAgencyCommunication
          ? requestedAction
          : 'profile'
      if (!Number.isInteger(memberUserId) || memberUserId <= 0) {
        this.routeIntentSerial += 1
        return false
      }

      const intentKey = `${memberUserId}:${action}:${query.conversationId || ''}`
      if (this.handledRouteIntent === intentKey) return false
      this.handledRouteIntent = intentKey
      const requestSerial = ++this.routeIntentSerial
      const featureGeneration = this.featureStateGeneration
      const isCurrent = () =>
        requestSerial === this.routeIntentSerial &&
        featureGeneration === this.featureStateGeneration &&
        !this.featureDisabled

      if (action === 'chat') {
        const hasTrustedConversationId =
          query.conversationId !== undefined &&
          query.conversationId !== null &&
          query.conversationId !== ''
        if (hasTrustedConversationId) {
          await this.loadConversations()
          if (!isCurrent()) return false
        }
        const conversation = findConversationByTrustedId(this.conversations, query.conversationId)
        if (conversation) {
          await this.openConversation(conversation)
          if (!isCurrent()) return false
          return true
        }
      }

      try {
        const response = await getLeaderProfile(memberUserId)
        if (!isCurrent()) return false
        const profile = normalizeLeaderProfile(normalizePhaseThreeResponse(response))
        if (action === 'profile') {
          this.selectedProfile = profile
          this.profileError = ''
          this.profileDialogVisible = true
        } else {
          await this.openContact(profile)
          if (!isCurrent()) return false
          this.contactForm.contactType = action === 'contact' ? 'invitation' : 'message'
        }
        return true
      } catch (error) {
        if (!isCurrent()) return false
        if (!this.handleFeatureDisabled(error)) {
          this.$message.error('無法開啟這位應徵者的公開簡介。')
        }
        return false
      }
    },
    async showProfile(row) {
      if (this.featureDisabled) return false
      const requestSerial = ++this.profileRequestSerial
      const featureGeneration = this.featureStateGeneration
      this.selectedProfile = null
      this.profileError = ''
      this.profileDialogVisible = true
      this.profileLoading = true
      try {
        const profile = normalizeLeaderProfile(
          normalizePhaseThreeResponse(
            await getLeaderProfile(row.userId || row.userID || row.id || row.ID)
          )
        )
        if (
          requestSerial !== this.profileRequestSerial ||
          featureGeneration !== this.featureStateGeneration
        ) {
          return false
        }
        this.selectedProfile = profile
        return true
      } catch (error) {
        if (requestSerial !== this.profileRequestSerial) return false
        if (!this.handleFeatureDisabled(error)) this.profileError = '無法載入公開履歷。'
        return false
      } finally {
        if (requestSerial === this.profileRequestSerial) this.profileLoading = false
      }
    },
    async openContact(row) {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      const requestSerial = ++this.travelRequestSerial
      const featureGeneration = this.featureStateGeneration
      this.profileDialogVisible = false
      this.contactTarget = row
      this.contactForm = {
        contactType: 'message',
        travelId: null,
        title: '',
        workDates: [],
        expiresAt: '',
        content: '',
      }
      this.contactDialogVisible = true
      this.$nextTick(() => this.$refs.contactForm && this.$refs.contactForm.clearValidate())
      this.travelLoading = true
      this.travelError = ''
      try {
        const result = toPhaseThreePagedResult(await getAgencyFeatureTravels())
        if (
          requestSerial !== this.travelRequestSerial ||
          featureGeneration !== this.featureStateGeneration
        ) {
          return false
        }
        this.travels = result.items
        return true
      } catch (error) {
        if (requestSerial !== this.travelRequestSerial) return false
        this.travels = []
        if (!this.handleFeatureDisabled(error)) {
          this.travelError = '無法取得可邀約的旅行團。'
        }
      } finally {
        if (requestSerial === this.travelRequestSerial) this.travelLoading = false
      }
    },
    submitContact() {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      this.$refs.contactForm.validate(async valid => {
        if (!valid || !this.contactTarget) return

        const requestSerial = ++this.contactRequestSerial
        const featureGeneration = this.featureStateGeneration
        this.contactSubmitting = true
        try {
          if (this.contactForm.contactType === 'invitation') {
            const result = normalizePhaseThreeResponse(
              await createAgencyInvitation({
                memberUserId: this.requirePositiveId(
                  this.contactTarget.userId || this.contactTarget.userID
                ),
                travelId: this.requirePositiveId(this.contactForm.travelId),
                title: this.contactForm.title,
                content: this.contactForm.content,
                workStartDate: this.toTaipeiDateTime(this.contactForm.workDates[0]),
                workEndDate: this.toTaipeiDateTime(this.contactForm.workDates[1], true),
                expiresAt: this.toTaipeiDateTime(this.contactForm.expiresAt),
                clientMessageId: this.createClientMessageId(),
              })
            )
            this.requireDeliveredInvitation(result)
          } else {
            normalizePhaseThreeResponse(
              await createAgencyConversation({
                memberUserId: this.requirePositiveId(
                  this.contactTarget.userId || this.contactTarget.userID
                ),
                travelId: this.requirePositiveId(this.contactForm.travelId),
                messageType: 'text',
                content: this.contactForm.content,
                mediaId: null,
                invitationId: null,
                clientMessageId: this.createClientMessageId(),
              })
            )
          }
          if (
            requestSerial !== this.contactRequestSerial ||
            featureGeneration !== this.featureStateGeneration
          ) {
            return
          }
          this.$message.success(
            this.contactForm.contactType === 'invitation' ? '邀約已送出' : '訊息已送出'
          )
          this.contactDialogVisible = false
          this.loadConversations()
          if (this.contactForm.contactType === 'invitation') this.loadInvitations()
        } catch (error) {
          if (requestSerial !== this.contactRequestSerial) return
          if (!this.handleFeatureDisabled(error)) {
            this.$message.error('訊息未送出，請確認會員與旅行團資格後再試。')
          }
        } finally {
          if (requestSerial === this.contactRequestSerial) this.contactSubmitting = false
        }
      })
    },
    createClientMessageId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID()
      }
      if (!window.crypto || typeof window.crypto.getRandomValues !== 'function') {
        throw new Error('secure_random_unavailable')
      }

      const bytes = new Uint8Array(16)
      window.crypto.getRandomValues(bytes)
      bytes[6] = (bytes[6] & 0x0f) | 0x40
      bytes[8] = (bytes[8] & 0x3f) | 0x80
      const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
        16,
        20
      )}-${hex.slice(20)}`
    },
    requirePositiveId(value) {
      const id = Number(value)
      if (!Number.isInteger(id) || id <= 0) throw new Error('invalid_identifier')
      return id
    },
    toTaipeiDateTime(value, endOfDay = false) {
      if (typeof value !== 'string' || !value.trim()) throw new Error('invalid_datetime')
      const trimmed = value.trim().replace(' ', 'T')
      const withTime = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
        ? `${trimmed}T${endOfDay ? '23:59:59' : '00:00:00'}`
        : trimmed
      return /(?:Z|[+-]\d{2}:\d{2})$/.test(withTime) ? withTime : `${withTime}+08:00`
    },
    requireDeliveredInvitation(result) {
      const invitation = deliveredInvitation(result)
      if (!invitation) throw new Error('invitation_not_delivered')
      return invitation
    },
    async openHistory() {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      this.historyDialogVisible = true
      this.historyError = ''
      await Promise.all([this.loadConversations(), this.loadInvitations()])
    },
    async loadConversations() {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      const requestSerial = this.conversationListRequestGuard.begin()
      this.historyLoading = true
      try {
        const result = toPhaseThreePagedResult(await getAgencyConversations())
        if (!this.conversationListRequestGuard.isCurrent(requestSerial)) return false
        let conversations = result.items.map(normalizeAgencyConversation)
        const activeConversationId = this.currentConversationId()
        if (this.chatDialogVisible && this.activeConversationMarkedRead && activeConversationId) {
          conversations = synchronizeConversationUnreadCount(conversations, activeConversationId, 0)
        }
        this.conversations = conversations
        return true
      } catch (error) {
        if (!this.conversationListRequestGuard.isCurrent(requestSerial)) return false
        this.conversations = []
        if (!this.handleFeatureDisabled(error)) {
          this.historyError = '無法載入邀約與聊天歷史。'
        }
        return false
      } finally {
        if (this.conversationListRequestGuard.isCurrent(requestSerial)) {
          this.historyLoading = false
        }
      }
    },
    async loadInvitations() {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      const requestSerial = ++this.invitationRequestSerial
      const featureGeneration = this.featureStateGeneration
      this.invitationLoading = true
      try {
        const result = toPhaseThreePagedResult(await getAgencyInvitations())
        if (
          requestSerial !== this.invitationRequestSerial ||
          featureGeneration !== this.featureStateGeneration
        ) {
          return false
        }
        this.invitations = result.items
        return true
      } catch (error) {
        if (requestSerial !== this.invitationRequestSerial) return false
        this.invitations = []
        if (!this.handleFeatureDisabled(error)) {
          this.historyError = this.historyError || '無法載入邀約紀錄。'
        }
        return false
      } finally {
        if (requestSerial === this.invitationRequestSerial) this.invitationLoading = false
      }
    },
    canCancelInvitation,
    invitationStatusLabel,
    invitationStatusType,
    async cancelInvitation(invitation) {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      const invitationId = invitation.id || invitation.invitationId
      if (!invitationId || !canCancelInvitation(invitation)) return

      try {
        await this.$confirm('撤回後將無法恢復此邀約，確定繼續？', '撤回邀約', {
          type: 'warning',
          confirmButtonText: '撤回',
          cancelButtonText: '取消',
        })
        normalizePhaseThreeResponse(await cancelAgencyInvitation(invitationId))
        this.$message.success('邀約已撤回')
        await this.loadInvitations()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close' && !this.handleFeatureDisabled(error)) {
          this.$message.error('無法撤回邀約，請重新載入後再試。')
        }
      }
    },
    async openConversation(conversation) {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      const conversationId = conversation.id || conversation.conversationId || conversation.roomId
      if (!conversationId) {
        this.$message.error('聊天室資料不完整。')
        return
      }

      const request = this.conversationRequestGuard.begin(conversationId)
      this.selectedConversation = conversation
      this.chatDialogVisible = true
      this.chatInput = ''
      this.chatError = ''
      this.activeConversationMarkedRead = false
      this.chatSubmitting = false
      this.failedImageFile = null
      this.failedChatEnvelope = null
      const loaded = await this.loadConversationMessages(conversationId, request)
      if (!loaded || !this.conversationRequestGuard.isCurrent(request)) return
      this.startChatUpdates()
      try {
        normalizePhaseThreeResponse(await markAgencyConversationRead(conversationId))
        if (this.conversationRequestGuard.isCurrent(request)) {
          this.activeConversationMarkedRead = true
          this.setConversationUnreadCount(conversationId, 0)
        }
      } catch (error) {
        // History remains available when only the read receipt endpoint is unavailable.
        this.handleFeatureDisabled(error)
      }
    },
    async loadConversationMessages(conversationId, request) {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      this.chatLoading = true
      this.chatError = ''
      try {
        const result = toPhaseThreePagedResult(
          await getAgencyConversationMessages(conversationId, { limit: 100 })
        )
        if (!this.conversationRequestGuard.isCurrent(request)) return false
        this.messages = result.items
        this.synchronizeProtectedImages()
        return true
      } catch (error) {
        if (!this.conversationRequestGuard.isCurrent(request)) return false
        this.messages = []
        if (!this.handleFeatureDisabled(error)) this.chatError = '無法載入聊天紀錄。'
        return false
      } finally {
        if (this.conversationRequestGuard.isCurrent(request)) this.chatLoading = false
      }
    },
    currentConversationId() {
      if (!this.selectedConversation) return ''
      return String(
        this.selectedConversation.id ||
          this.selectedConversation.conversationId ||
          this.selectedConversation.roomId ||
          ''
      )
    },
    async sendConversationPayload(envelope) {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      if (!this.selectedConversation || this.conversationReadOnly || !envelope) return false
      const conversationId = this.currentConversationId()
      if (!conversationId) return

      const request = this.conversationRequestGuard.begin(conversationId)
      this.chatSubmitting = true
      try {
        normalizePhaseThreeResponse(await sendAgencyConversationMessage(conversationId, envelope))
        if (!this.conversationRequestGuard.isCurrent(request)) return false
        if (
          this.failedChatEnvelope &&
          this.failedChatEnvelope.clientMessageId === envelope.clientMessageId
        ) {
          this.failedChatEnvelope = null
        }
        await this.loadConversationMessages(conversationId, request)
        if (this.conversationRequestGuard.isCurrent(request)) await this.loadConversations()
        return true
      } catch (error) {
        if (this.conversationRequestGuard.isCurrent(request)) {
          if (!this.handleFeatureDisabled(error)) {
            this.failedChatEnvelope = Object.freeze({ ...envelope })
            this.$message.error('訊息未送出，請確認會員目前仍具有有效資格。')
          }
        }
        return false
      } finally {
        if (this.conversationRequestGuard.isCurrent(request)) this.chatSubmitting = false
      }
    },
    async sendConversationMessage() {
      const content = this.chatInput.trim()
      if (!content) return
      const sent = await this.sendConversationPayload({
        messageType: 'text',
        content,
        mediaId: null,
        clientMessageId: this.createClientMessageId(),
      })
      if (sent && this.chatInput.trim() === content) this.chatInput = ''
    },
    async retryFailedChatMessage() {
      if (this.failedChatEnvelope) {
        await this.sendConversationPayload(this.failedChatEnvelope)
      }
    },
    selectChatImage(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (file) void this.uploadAndSendImage(file)
    },
    async uploadAndSendImage(file) {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      if (this.conversationReadOnly || this.chatUploading || this.chatSubmitting) return
      try {
        validateTourLeaderChatImage(file)
      } catch (error) {
        this.$message.error(error.message)
        return
      }

      const conversationId = this.currentConversationId()
      if (!conversationId) return
      const uploadGeneration = ++this.chatUploadGeneration
      this.chatUploading = true
      this.failedImageFile = null
      try {
        const uploaded = normalizePhaseThreeResponse(
          await uploadAgencyConversationMedia(conversationId, file)
        )
        const mediaId =
          uploaded && (uploaded.mediaId || uploaded.MediaId || uploaded.MediaID || uploaded.id)
        if (mediaId === undefined || mediaId === null || mediaId === '') {
          throw new Error('media_id_missing')
        }
        if (
          uploadGeneration !== this.chatUploadGeneration ||
          conversationId !== this.currentConversationId() ||
          !this.chatDialogVisible
        ) {
          return
        }
        await this.sendConversationPayload({
          messageType: 'image',
          content: null,
          mediaId,
          clientMessageId: this.createClientMessageId(),
        })
      } catch (error) {
        if (uploadGeneration === this.chatUploadGeneration) {
          if (!this.handleFeatureDisabled(error)) {
            this.failedImageFile = file
            this.$message.error('圖片上傳失敗，請重試。')
          }
        }
      } finally {
        if (uploadGeneration === this.chatUploadGeneration) this.chatUploading = false
      }
    },
    retryImageUpload() {
      if (this.failedImageFile) void this.uploadAndSendImage(this.failedImageFile)
    },
    isImageMessage(message) {
      const messageType = String(
        (message && (message.messageType || message.MessageType)) || ''
      ).toLowerCase()
      return messageType === 'image'
    },
    chatMediaId(message) {
      if (!message) return null
      return message.mediaId !== undefined
        ? message.mediaId
        : message.MediaID !== undefined
          ? message.MediaID
          : message.MediaId
    },
    chatMediaKey(message) {
      return `${this.currentConversationId()}:${String(this.chatMediaId(message))}`
    },
    synchronizeProtectedImages() {
      const retained = new Set(
        this.messages
          .filter(message => this.isImageMessage(message) && this.chatMediaId(message) != null)
          .map(message => this.chatMediaKey(message))
      )
      Object.keys(this.chatImageUrls).forEach(key => {
        if (retained.has(key)) return
        URL.revokeObjectURL(this.chatImageUrls[key])
        this.$delete(this.chatImageUrls, key)
      })
      Object.keys(this.chatImageLoading).forEach(key => {
        if (!retained.has(key)) this.$delete(this.chatImageLoading, key)
      })
      Object.keys(this.chatImageErrors).forEach(key => {
        if (!retained.has(key)) this.$delete(this.chatImageErrors, key)
      })
      this.messages.forEach(message => {
        const key = this.chatMediaKey(message)
        if (
          this.isImageMessage(message) &&
          this.chatMediaId(message) != null &&
          !this.chatImageUrls[key] &&
          !this.chatImageLoading[key]
        ) {
          void this.loadProtectedImage(message)
        }
      })
    },
    async loadProtectedImage(message) {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      const conversationId = this.currentConversationId()
      const mediaId = this.chatMediaId(message)
      if (!conversationId || mediaId === null || mediaId === undefined) return
      const key = this.chatMediaKey(message)
      const generation = this.chatImageGeneration
      this.$set(this.chatImageLoading, key, true)
      this.$set(this.chatImageErrors, key, '')
      try {
        const response = await downloadAgencyConversationMedia(conversationId, mediaId)
        const blob = protectedMediaBody(response)
        const url = URL.createObjectURL(blob)
        if (
          generation !== this.chatImageGeneration ||
          !this.chatDialogVisible ||
          conversationId !== this.currentConversationId() ||
          !this.messages.some(current => this.chatMediaKey(current) === key)
        ) {
          URL.revokeObjectURL(url)
          return
        }
        if (this.chatImageUrls[key]) URL.revokeObjectURL(this.chatImageUrls[key])
        this.$set(this.chatImageUrls, key, url)
      } catch (error) {
        if (generation === this.chatImageGeneration) {
          if (!this.handleFeatureDisabled(error)) {
            this.$set(this.chatImageErrors, key, '圖片載入失敗')
          }
        }
      } finally {
        if (generation === this.chatImageGeneration) {
          this.$set(this.chatImageLoading, key, false)
        }
      }
    },
    async refreshActiveConversation() {
      if (!this.canUseAgencyCommunication || this.featureDisabled) return false
      const conversationId = this.currentConversationId()
      if (!conversationId || !this.chatDialogVisible || this.chatSubmitting || this.chatUploading) {
        return
      }
      const request = this.conversationRequestGuard.begin(conversationId)
      const result = await refreshCurrentConversationReadState({
        loadMessages: () => this.loadConversationMessages(conversationId, request),
        markRead: () =>
          markAgencyConversationRead(conversationId).then(normalizePhaseThreeResponse),
        isCurrent: () => this.conversationRequestGuard.isCurrent(request),
      })
      if (result.error && this.handleFeatureDisabled(result.error)) return false
      if (!result.refreshed || !this.conversationRequestGuard.isCurrent(request)) return
      if (result.markedRead) {
        this.activeConversationMarkedRead = true
        this.setConversationUnreadCount(conversationId, 0)
      }
      await this.loadConversations()
      if (result.markedRead && this.conversationRequestGuard.isCurrent(request)) {
        this.setConversationUnreadCount(conversationId, 0)
      }
    },
    setConversationUnreadCount(conversationId, unreadCount) {
      if (this.currentConversationId() === String(conversationId) && this.selectedConversation) {
        this.$set(this.selectedConversation, 'unreadCount', unreadCount)
      }
      this.conversations = synchronizeConversationUnreadCount(
        this.conversations,
        conversationId,
        unreadCount
      )
    },
    handleChatRealtimeEvent(event) {
      if (!this.canUseAgencyCommunication) return false
      const eventConversationId = String((event && event.conversationId) || '')
      if (
        event &&
        (event.type === 'socket.connected' ||
          ((event.type === 'message.created' || event.type === 'conversation.updated') &&
            eventConversationId === this.currentConversationId()))
      ) {
        void this.refreshActiveConversation()
      }
    },
    startChatUpdates() {
      if (!this.canUseAgencyCommunication) return false
      this.stopChatUpdates()
      this.unsubscribeChatSocket = tourLeaderAgencyRealtime.subscribe(event =>
        this.handleChatRealtimeEvent(event)
      )
      this.releaseChatSocket = tourLeaderAgencyRealtime.retain()
      this.chatPollTimer = setInterval(() => void this.refreshActiveConversation(), 30000)
    },
    stopChatUpdates() {
      clearInterval(this.chatPollTimer)
      this.chatPollTimer = null
      if (this.unsubscribeChatSocket) this.unsubscribeChatSocket()
      if (this.releaseChatSocket) this.releaseChatSocket()
      this.unsubscribeChatSocket = null
      this.releaseChatSocket = null
    },
    closeConversation() {
      this.stopChatUpdates()
      this.conversationRequestGuard.invalidate()
      this.chatImageGeneration += 1
      this.chatUploadGeneration += 1
      revokeProtectedMediaUrls(this.chatImageUrls)
      this.chatImageUrls = {}
      this.chatImageLoading = {}
      this.chatImageErrors = {}
      this.selectedConversation = null
      this.messages = []
      this.chatInput = ''
      this.chatError = ''
      this.activeConversationMarkedRead = false
      this.failedImageFile = null
      this.failedChatEnvelope = null
      this.chatUploading = false
      this.chatSubmitting = false
    },
    isAgencyMessage(message) {
      const senderType = message && (message.senderType || message.senderRole)
      return senderType === 'Agency' || senderType === 'agency' || senderType === 1
    },
    messageSenderLabel(message) {
      if (this.isAgencyMessage(message)) return '旅行社'
      const senderType = message && (message.senderType || message.senderRole)
      if (senderType === 'System' || senderType === 'system' || senderType === 3) return '系統'
      return '領隊／導遊'
    },
    displayName(profile) {
      if (!profile) return ''
      return profile.nickName
        ? `${profile.name || ''}（${profile.nickName}）`
        : profile.name || '未提供'
    },
    roleLabel(profile) {
      const role = profile.role || profile.accountType || profile.identity
      if (role === 'leader' || role === 4) return '領隊'
      if (role === 'guide' || role === 5) return '導遊'
      return profile.roleName || '領隊／導遊'
    },
    basicInfo(profile) {
      return (
        [profile.genderLabel || profile.gender, profile.ageRange || profile.age]
          .filter(Boolean)
          .join(' / ') || '未提供'
      )
    },
    listText(values) {
      if (!values) return '未提供'
      if (Array.isArray(values)) {
        const labels = values.map(value =>
          typeof value === 'object' ? value.name || value.label : value
        )
        return labels.filter(Boolean).join('、') || '未提供'
      }
      return String(values)
    },
  },
  computed: {
    canManageSettings() {
      return hasAnyRole(this.$store.getters.roles, TOUR_LEADER_SETTINGS_ADMIN_ROLES)
    },
    canUseAgencyCommunication() {
      return canUseTourLeaderAgencyCommunication(this.$store.getters.roles)
    },
    conversationReadOnly() {
      return isConversationReadOnly(this.selectedConversation)
    },
    groupedChatMessages() {
      const items = []
      let lastDay = ''
      this.messages.forEach((message, index) => {
        const stamp = String((message && (message.createdAt || message.sentAt)) || '')
        const day = stamp.slice(0, 10)
        if (day && day !== lastDay) {
          lastDay = day
          items.push({ divider: true, key: `day-${day}-${index}`, label: day })
        }
        items.push({
          divider: false,
          key: message.id || message.messageId || message.clientMessageId || `row-${index}`,
          message,
        })
      })
      return items
    },
  },
}
</script>

<style lang="scss" scoped>
.leader-hunter-page {
  .page-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;

    h2 {
      margin: 0 0 6px;
    }

    p {
      margin: 0;
      color: #606266;
    }
  }

  .heading-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .jobs-entry {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .filter-card,
  .load-alert {
    margin-bottom: 16px;
  }

  .profile-cell {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .secondary-text {
    color: #909399;
  }

  .form-error {
    color: #f56c6c;
    font-size: 12px;
  }

  .banner-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 14px 0;
    color: #606266;
  }

  .danger-action {
    color: #f56c6c;
  }

}

// Element dialogs keep this page's scope on their wrapper, including when
// append-to-body moves the chat wrapper outside the page container.
@media (max-width: 768px) {
  ::v-deep .el-dialog {
    display: flex;
    flex-direction: column;
    max-width: calc(100vw - 24px);
    max-height: 92vh;
    margin-top: 4vh !important;
    margin-bottom: 4vh;
  }

  ::v-deep .el-dialog__header,
  ::v-deep .el-dialog__footer {
    flex-shrink: 0;
  }

  ::v-deep .el-dialog__body {
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    overflow-wrap: anywhere;
  }

  ::v-deep .el-dialog__footer {
    padding: 12px 16px;
  }

  ::v-deep .el-dialog__footer > span,
  ::v-deep .el-dialog__footer > div {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }

  ::v-deep .el-dialog__footer .el-button + .el-button {
    margin-left: 0;
  }

  ::v-deep .el-dialog .el-form-item__label {
    display: block;
    float: none;
    text-align: left;
    line-height: 28px;
  }

  ::v-deep .el-dialog .el-form-item__content {
    margin-left: 0 !important;
  }
}
</style>

<!-- 聊天室 dialog 使用 append-to-body（DOM 會被搬到 body 下），scoped 祖先選擇器會失效，
     因此聊天室樣式放在全域、以 dialog 自身 custom-class（hunter-chat-dialog）作為祖先。 -->
<style>
.hunter-chat-dialog .hunter-chat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  background: #f5f7fa;
  color: #303133;
}

.hunter-chat-dialog .hunter-chat-name {
  font-size: 15px;
  font-weight: 600;
}

.hunter-chat-dialog .hunter-chat-divider {
  color: #c0c4cc;
}

.hunter-chat-dialog .hunter-chat-travel {
  overflow: hidden;
  font-size: 13px;
  color: #606266;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hunter-chat-dialog .message-list {
  min-height: 260px;
  max-height: 460px;
  overflow-y: auto;
  margin: 0 0 12px;
  padding: 12px 16px;
  background: #fff;
}

.hunter-chat-dialog .hunter-chat-date {
  margin: 6px 0 12px;
  text-align: center;
}

.hunter-chat-dialog .empty-message {
  padding: 70px 0;
  text-align: center;
  color: #909399;
}

.hunter-chat-dialog .message-item {
  max-width: 80%;
  margin-bottom: 14px;
}

.hunter-chat-dialog .message-agency {
  margin-left: auto;
}

.hunter-chat-dialog .message-sender {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #606266;
}

.hunter-chat-dialog .sender-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e6e8eb;
  color: #606266;
  font-size: 12px;
}

.hunter-chat-dialog .message-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.hunter-chat-dialog .message-agency .message-row {
  flex-direction: row-reverse;
}

.hunter-chat-dialog .message-content {
  display: inline-block;
  padding: 6px 10px;
  border-radius: 6px;
  background: #fff8e0;
  color: #42443f;
  font-size: 15px;
  letter-spacing: 0.05em;
  line-height: 22px;
  white-space: pre-wrap;
  word-break: break-word;
}

.hunter-chat-dialog .message-agency .message-content {
  background: #f7f7f7;
}

.hunter-chat-dialog .message-image-body {
  padding: 0;
  background: transparent;
}

.hunter-chat-dialog .message-time {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.hunter-chat-dialog .chat-image {
  display: block;
  width: auto;
  max-width: 320px;
  max-height: 360px;
  border-radius: 6px;
  object-fit: contain;
}

.hunter-chat-dialog .chat-retry-row {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.hunter-chat-dialog .el-input-group__append button.el-button {
  background: #42d885;
  border-color: #42d885;
  color: #fff;
}

.hunter-chat-dialog .visually-hidden-file {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
